import { createSlice } from "@reduxjs/toolkit";
import { dispatch } from "../store";
import { tradeMate } from "../../api/endpoints/tradeMate";
import type { TradeMateAccountState } from "../../types/tradeMate";

function getLatestTradeMateBalance(stats: any, mt: any) {
  // currentBalance is Trade Mate's own figure, stored on the Analysis row. The
  // daily growth series is a reconstruction — the account size plus the profit
  // of every trade we hold — so a trade recorded twice, or one whose event
  // never arrived, prices the account wrongly. It is a last resort, not a
  // first choice: a balance shown to a trader should be the one the platform
  // holding their money reports.
  const latestGrowthBalance = Array.isArray(stats?.dailyGrowth) && stats.dailyGrowth.length > 0
    ? Number(stats.dailyGrowth[stats.dailyGrowth.length - 1]?.balance ?? 0)
    : 0;

  return Number(
    stats?.currentBalance ||
      stats?.balance ||
      mt?.currentBalance ||
      stats?.equity ||
      mt?.currentEquity ||
      mt?.balance ||
      latestGrowthBalance ||
      0,
  );
}

// Single source of truth for a TradeMate account's derived values. Every page
// (Dashboard, Trading Accounts, Analytics) reads the entry produced here so the
// same account never shows different balance / limits depending on the screen —
// mirroring how MatchTrader normalizes once in its reducer and pages read it
// straight through.
function normalizeTradeMateAccountEntry(entry: any) {
  const mt = entry?.mtAccount || {};
  const stats = entry?.metaStats || {};
  const liveBalance = getLatestTradeMateBalance(stats, mt);
  const liveEquity = Number(stats.currentEquity ?? stats.equity ?? liveBalance ?? mt.balance ?? 0) || null;
  const initialBalance = Number(
    stats.initialBalance ??
      stats.deposits ??
      mt.balance ??
      liveBalance ??
      0,
  );
  const liveGainCandidates = [
    stats.gain,
    stats.dailyGain,
    stats.monthlyGain,
    stats.periods?.today?.gain,
    initialBalance > 0 ? ((liveBalance - initialBalance) / initialBalance) * 100 : 0,
  ]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value !== 0);
  const liveGain = liveGainCandidates[0] ?? Number(stats.gain ?? 0);

  // Trade-derived stats — computed once here instead of separately (and
  // divergently) on each page.
  const trades = Array.isArray(entry?.trades) ? entry.trades : [];
  const activeTrades = trades.filter((trade: any) => trade?.type !== "DEAL_TYPE_BALANCE");
  const profits = activeTrades.map((trade: any) =>
    Number(trade.profit ?? trade.netProfit ?? trade.pnl ?? 0),
  );
  const positiveTrades = profits.filter((p: number) => p > 0);
  const negativeTrades = profits.filter((p: number) => p < 0);
  const averageWin = positiveTrades.length > 0
    ? positiveTrades.reduce((sum: number, v: number) => sum + v, 0) / positiveTrades.length
    : Number(stats.averageWin ?? 0);
  const averageLoss = negativeTrades.length > 0
    ? negativeTrades.reduce((sum: number, v: number) => sum + v, 0) / negativeTrades.length
    : Number(stats.averageLoss ?? 0);
  const wonTradesPercent = activeTrades.length > 0
    ? (positiveTrades.length / activeTrades.length) * 100
    : Number(stats.wonTradesPercent ?? 0);
  const profit = stats.profit != null
    ? Number(stats.profit)
    : (Number.isFinite(liveBalance) && Number.isFinite(initialBalance) ? liveBalance - initialBalance : 0);

  return {
    ...entry,
    currentBalance: liveBalance || null,
    currentEquity: liveEquity,
    mtAccount: {
      ...mt,
      // Keep the REAL program (twoPhase/onePhase/velocity/instant*) so the
      // dashboard's getProgramRules() computes correct daily-loss/max-loss/
      // profit-target limits. Never coerce to the literal "trademate".
      challengeType: mt.challengeType ?? null,
      challengePhase: mt.challengePhase ?? null,
      balance: mt.balance != null ? Number(mt.balance) : null,
      currentBalance: liveBalance || null,
      currentEquity: liveEquity,
    },
    metaStats: {
      ...stats,
      balance: liveBalance,
      currentBalance: liveBalance,
      equity: liveEquity ?? liveBalance,
      currentEquity: liveEquity,
      gain: liveGain,
      dailyGain: stats.dailyGain ?? liveGain,
      monthlyGain: stats.monthlyGain ?? liveGain,
      initialBalance,
      deposits: stats.deposits ?? initialBalance,
      profit,
      trades: activeTrades.length || Number(stats.trades ?? 0),
      lots: activeTrades.reduce((sum: number, trade: any) => sum + Number(trade.volume ?? 0), 0) || Number(stats.lots ?? 0),
      wonTrades: positiveTrades.length || Number(stats.wonTrades ?? 0),
      lostTrades: negativeTrades.length || Number(stats.lostTrades ?? 0),
      wonTradesPercent,
      averageWin,
      averageLoss,
      bestTrade: positiveTrades.length > 0 ? Math.max(...positiveTrades) : Number(stats.bestTrade ?? 0),
      worstTrade: profits.length > 0 ? Math.min(...profits) : Number(stats.worstTrade ?? 0),
      dailyGrowth: Array.isArray(stats.dailyGrowth) ? stats.dailyGrowth : [],
      periods: stats.periods ?? {
        today: { profit: Math.max(0, liveBalance - initialBalance), gain: liveGain },
      },
      tradingStartBrokerTime: (
        activeTrades[0]?.openTime ||
        stats.tradingStartBrokerTime ||
        stats.creationDate ||
        ""
      ).toString().split("T")[0],
    },
  };
}

const initialState: TradeMateAccountState = {
  error: null,
  accounts: [],
  accountsData: {},
  loading: false,
};

const tradeMateAccount = createSlice({
  name: "tradeMateAccount",
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    startLoading(state) {
      state.loading = true;
    },
    getAccountsSuccess(state, action) {
      state.accounts = action.payload;
      state.loading = false;
    },
    getAccountsDataSuccess(state, action) {
      state.accountsData = action.payload;
    },
  },
});

export const { hasError, getAccountsSuccess, getAccountsDataSuccess } = tradeMateAccount.actions;

export function getTradeMateAccounts(userId: string, options: { skipCatchUp?: boolean } = {}) {
  return async () => {
    try {
      dispatch(tradeMateAccount.actions.startLoading());
      const response = await tradeMate.getMyAccounts(userId, options);
      const data: any = response?.data;

      // New meta-shaped response:
      //   { accounts: { "<login>": { mtAccount, metaStats, trades, analysis } } }
      if (
        response.success &&
        data &&
        !Array.isArray(data) &&
        data.accounts &&
        !Array.isArray(data.accounts)
      ) {
        const accountsData = data.accounts as Record<string, any>;
        const normalizedAccountsData = Object.fromEntries(
          Object.entries(accountsData).map(([login, entry]) => [
            login,
            normalizeTradeMateAccountEntry(entry),
          ]),
        );
        const accounts = Object.values(normalizedAccountsData).map((entry: any) => {
          const mt = entry.mtAccount || {};
          const stats = entry.metaStats || {};
          const liveBalance = getLatestTradeMateBalance(stats, mt);
          return {
            ...mt,
            id: mt.id ?? mt.login,
            accountId: mt.accountId || mt.accountNumber || mt.login,
            login: String(mt.login),
            balance: mt.balance != null ? Number(mt.balance) : null,
            currentBalance: liveBalance || null,
            currentEquity: Number(stats.currentEquity ?? stats.equity ?? liveBalance ?? mt.balance ?? 0) || null,
            liveUpdatedAt: mt.updatedAt || null,
          };
        });
        dispatch(tradeMateAccount.actions.getAccountsSuccess(accounts));
        dispatch(tradeMateAccount.actions.getAccountsDataSuccess(normalizedAccountsData));
      } else if (response.success && data) {
        // Legacy flat-array response.
        const raw = Array.isArray(data) ? data : data.accounts || [];
        const accounts = raw.map((account: any) => ({
          ...account,
          accountId: account.accountId || account.accountNumber || account.login,
          login: String(account.login),
          balance: account.balance != null ? Number(account.balance) : null,
          currentBalance:
            account.currentBalance != null ? Number(account.currentBalance) : null,
          currentEquity:
            account.currentEquity != null
              ? Number(account.currentEquity)
              : account.currentBalance != null
                ? Number(account.currentBalance)
                : account.balance != null
                  ? Number(account.balance)
                  : null,
          liveUpdatedAt: account.liveUpdatedAt || null,
        }));
        dispatch(tradeMateAccount.actions.getAccountsSuccess(accounts));
        dispatch(tradeMateAccount.actions.getAccountsDataSuccess({}));
      } else {
        dispatch(tradeMateAccount.actions.getAccountsSuccess([]));
        dispatch(tradeMateAccount.actions.getAccountsDataSuccess({}));
      }
      dispatch(tradeMateAccount.actions.hasError(null));
    } catch (err: any) {
      console.error("Error fetching Trade Mate accounts:", err);
      dispatch(tradeMateAccount.actions.getAccountsSuccess([]));
      dispatch(tradeMateAccount.actions.getAccountsDataSuccess({}));
      dispatch(tradeMateAccount.actions.hasError(err.message || "Failed to fetch accounts"));
    }
  };
}

export default tradeMateAccount.reducer;
